import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* CONFIG                                                              */
/* ------------------------------------------------------------------ */
export const IONIC_HERO_CONFIG = {
  headline: ['30+ YEARS', '1,200+ SALES', 'SINCE 1994'],
  supporting: '',
  cta: { label: 'Explore Ionic', href: '#capabilities' },
  autoplay: true,
  autoplayInterval: 5200,
  /* 5 columns; center column (index 2) survives the sweep and expands. */
  images: [
    { src: '/hero/asset5.webp', src400: '/hero/asset5-400w.webp', src800: '/hero/asset5-800w.webp', alt: 'Ionic employee meeting with homeowners',                 focal: 'center 42%' },
    { src: '/hero/asset2.webp', src400: '/hero/asset2-400w.webp', src800: '/hero/asset2-800w.webp', alt: 'Ionic employee helping clients discover an heirloom',    focal: 'center 40%' },
    { src: '/hero/asset3.webp', src400: '/hero/asset3-400w.webp', src800: '/hero/asset3-800w.webp', alt: 'Ionic team staging a modern home',                       focal: 'center 42%' }, // CENTER
    { src: '/hero/asset4.webp', src400: '/hero/asset4-400w.webp', src800: '/hero/asset4-800w.webp', alt: 'Ionic team packing and organizing the home',             focal: 'center 45%' },
    { src: '/hero/asset1.webp', src400: '/hero/asset1-400w.webp', src800: '/hero/asset1-800w.webp', alt: 'Ionic live estate sale checkout with team and shoppers', focal: 'center 40%' },
  ],
  centerIdx: 2,
  timing: {
    gridIn:       700,   // ms — grid fades / scales in
    gridZoom:     900,   // ms — grid scales from 0.72 → 1
    columnsSweep: 850,   // ms — alternating columns fly out vertically
    centerExpand: 900,   // ms — center column grows from 20% → 100% of viewport
    letterStagger: 42,   // ms — per-character delay on headline reveal
  },
};

/* Sweep direction per column: -1 up, +1 down, 0 stay.
   With 5 columns and center=index 2:
   outer cols (0,4) sweep up; inner cols (1,3) sweep down; center stays. */
const SWEEP = [-1, 1, 0, 1, -1];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Split a headline into per-character spans that animate in with a stagger.
   Accepts either a single string or an array of strings (each string
   becomes its own stacked line). Words are kept together (nowrap) so
   tokens like "1,200+" and "1994" never break across lines. */
const SplitHeadline = ({ text, active, stagger = 40, className = '' }) => {
  const lines = Array.isArray(text) ? text : [text];
  const flat = lines.join(' ');
  let charIdx = 0;
  return (
    <h1 className={className} aria-label={flat} data-testid="hero-headline">
      {lines.map((line, li) => {
        const words = line.split(' ');
        return (
          <span key={`line-${li}`} className="hero-line">
            {words.map((word, wi) => {
              const chars = word.split('');
              const wordEl = (
                <span key={`w-${li}-${wi}`} className="hero-word">
                  {chars.map((ch, ci) => {
                    const i = charIdx++;
                    return (
                      <span
                        key={`c-${li}-${wi}-${ci}`}
                        aria-hidden="true"
                        className="hero-letter"
                        style={{
                          transitionDelay: `${active ? i * stagger : 0}ms`,
                          transform: active ? 'translateY(0) rotateX(0deg)' : 'translateY(120%) rotateX(-40deg)',
                          opacity: active ? 1 : 0,
                        }}
                      >
                        {ch}
                      </span>
                    );
                  })}
                </span>
              );
              const spaceEl = wi < words.length - 1 ? (
                <span key={`s-${li}-${wi}`} aria-hidden="true" className="hero-letter hero-space">{'\u00A0'}</span>
              ) : null;
              if (spaceEl) charIdx++;
              return (
                <React.Fragment key={`f-${li}-${wi}`}>
                  {wordEl}
                  {spaceEl}
                </React.Fragment>
              );
            })}
          </span>
        );
      })}
    </h1>
  );
};

const IonicGridHero = ({ onOpenVault, config = IONIC_HERO_CONFIG }) => {
  const { images, headline, supporting, cta, autoplay, autoplayInterval, timing, centerIdx } = config;

  /**
   * Phase machine:
   *   'grid-in'       — small grid fades in
   *   'grid-zoom'     — grid scales up to fill the viewport
   *   'columns-sweep' — alternating columns fly out vertically
   *   'center-expand' — center column grows from 20%→100% of viewport
   *   'slider'        — thumbnail-driven slider takes over (crossfade)
   */
  const [phase, setPhase] = useState('grid-in');
  const [activeIdx, setActiveIdx] = useState(centerIdx);
  const [copyIn, setCopyIn] = useState(false);
  const [thumbsIn, setThumbsIn] = useState(false);
  const [scroll, setScroll] = useState({ y: 0, o: 1 });

  const timers = useRef([]);
  const rootRef = useRef(null);
  const autoRef = useRef(null);
  const reducedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);
  const queue = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
    return t;
  };

  /* Kick off the choreographed entrance (one-shot). */
  useEffect(() => {
    reducedRef.current = prefersReducedMotion();
    if (reducedRef.current) {
      setPhase('slider');
      setCopyIn(true);
      setThumbsIn(true);
      return;
    }
    const { gridIn, gridZoom, columnsSweep, centerExpand } = timing;
    queue(() => setPhase('grid-zoom'),      gridIn);
    queue(() => setPhase('columns-sweep'),  gridIn + gridZoom);
    queue(() => setCopyIn(true),            gridIn + gridZoom + columnsSweep - 150);
    queue(() => setPhase('center-expand'),  gridIn + gridZoom + columnsSweep);
    queue(() => setPhase('slider'),         gridIn + gridZoom + columnsSweep + centerExpand);
    queue(() => setThumbsIn(true),          gridIn + gridZoom + columnsSweep + centerExpand + 250);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Slider autoplay. */
  useEffect(() => {
    if (phase !== 'slider' || !autoplay || reducedRef.current) return;
    autoRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % images.length);
    }, autoplayInterval);
    return () => clearInterval(autoRef.current);
  }, [phase, autoplay, autoplayInterval, images.length]);

  /* Scroll drift + fade — no pinning; the rest of the page scrolls normally. */
  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!rootRef.current) return;
        const r = rootRef.current.getBoundingClientRect();
        const h = rootRef.current.offsetHeight;
        const p = Math.min(1, Math.max(0, -r.top / h));
        setScroll({ y: p * -80, o: Math.max(0, 1 - p * 1.4) });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* Thumbnail click. */
  const goTo = useCallback((i) => {
    if (phase !== 'slider') return;
    setActiveIdx(i);
    // Reset the autoplay clock after a manual choice.
    if (autoRef.current) clearInterval(autoRef.current);
    if (autoplay && !reducedRef.current) {
      autoRef.current = setInterval(() => {
        setActiveIdx((prev) => (prev + 1) % images.length);
      }, autoplayInterval);
    }
  }, [phase, autoplay, autoplayInterval, images.length]);

  /* --------- Per-column tile state ---------- */
  const getTile = (i) => {
    const isCenter = i === centerIdx;
    const dir = SWEEP[i];

    // Defaults — the 5-column grid at scale 1.
    let leftPct = i * 20;
    let widthPct = 20;
    let translateY = '0%';
    let opacity = 1;
    let z = isCenter ? 6 : 4;
    let visible = true;

    if (phase === 'grid-in') {
      opacity = 0;                    // fades in — grid-scale handled by wrapper
    } else if (phase === 'grid-zoom') {
      // handled by wrapper scale
    } else if (phase === 'columns-sweep') {
      if (!isCenter) translateY = dir === -1 ? '-115%' : '115%';
    } else if (phase === 'center-expand') {
      if (!isCenter) {
        opacity = 0;
        translateY = dir === -1 ? '-115%' : '115%';
      } else {
        leftPct = 0;
        widthPct = 100;
        z = 8;
      }
    } else if (phase === 'slider') {
      // Grid tiles fully retire — the slider layer takes over.
      if (isCenter) {
        leftPct = 0;
        widthPct = 100;
      } else {
        opacity = 0;
        translateY = dir === -1 ? '-115%' : '115%';
      }
      visible = false;
    }

    return { leftPct, widthPct, translateY, opacity, z, visible };
  };

  /* Wrapper scale for the grid-in / grid-zoom stages. */
  const wrapperScale = phase === 'grid-in' ? 0.72 : 1;
  const wrapperOpacity = phase === 'grid-in' ? 0 : phase === 'slider' ? 0 : 1;

  /* Mobile-only 3-column layout is authoritative ONLY while the grid is
     still a grid. Once the center column starts expanding, JS takes over. */
  const isGridPhase = phase === 'grid-in' || phase === 'grid-zoom' || phase === 'columns-sweep';

  return (
    <section
      ref={rootRef}
      data-testid="ionic-grid-hero"
      data-phase={phase}
      className={`relative bg-black text-white overflow-hidden ${isGridPhase ? 'hero--gridphase' : ''}`}
      style={{
        height: '100vh',
        minHeight: 620,
        transform: `translate3d(0, ${scroll.y}px, 0)`,
        opacity: scroll.o,
        willChange: 'transform, opacity',
      }}
    >
      {/* ============ LAYER A — 5-COLUMN CHOREOGRAPHED GRID ============ */}
      <div
        aria-hidden={phase === 'slider'}
        className="absolute inset-0"
        style={{
          transform: `scale(${wrapperScale})`,
          transformOrigin: 'center center',
          opacity: wrapperOpacity,
          transition: `transform ${timing.gridZoom}ms cubic-bezier(0.65, 0.05, 0.22, 1), opacity 700ms ease`,
          padding: 6,
        }}
      >
        <div className="relative w-full h-full">
          {images.map((img, i) => {
            const t = getTile(i);
            const sweepDur = phase === 'columns-sweep' ? timing.columnsSweep
              : phase === 'center-expand' ? timing.centerExpand
              : 700;
            /* Small stagger on the sweep so it feels choreographed, not synchronized. */
            const sweepDelay = phase === 'columns-sweep'
              ? Math.abs(i - centerIdx) === 2 ? 0 : 90
              : 0;
            return (
              <div
                key={img.src}
                data-testid={`hero-col-${i}`}
                className="absolute top-0 h-full overflow-hidden"
                style={{
                  left: `${t.leftPct}%`,
                  width: `${t.widthPct}%`,
                  transform: `translate3d(0, ${t.translateY}, 0)`,
                  opacity: t.opacity,
                  zIndex: t.z,
                  transition: [
                    `left ${timing.centerExpand}ms cubic-bezier(0.75, 0.05, 0.15, 1)`,
                    `width ${timing.centerExpand}ms cubic-bezier(0.75, 0.05, 0.15, 1)`,
                    `transform ${sweepDur}ms cubic-bezier(0.65, 0.05, 0.22, 1) ${sweepDelay}ms`,
                    `opacity 600ms ease`,
                  ].join(', '),
                  willChange: 'left, width, transform, opacity',
                  padding: 3,
                }}
              >
                <div className="w-full h-full overflow-hidden rounded-[2px]">
                  <img
                    src={img.src}
                    srcSet={`${img.src400} 400w, ${img.src800} 800w, ${img.src} 2000w`}
                    sizes="(max-width: 640px) 180px, (max-width: 1024px) 320px, 480px"
                    alt={img.alt}
                    draggable={false}
                    loading={i === centerIdx ? 'eager' : 'lazy'}
                    decoding="async"
                    className="w-full h-full object-cover select-none"
                    style={{ objectPosition: img.focal || 'center center' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ LAYER B — FULLSCREEN SLIDER (crossfade) ============ */}
      <div
        aria-hidden={phase !== 'slider'}
        className="absolute inset-0"
        style={{
          opacity: phase === 'slider' ? 1 : 0,
          transition: 'opacity 500ms ease',
          zIndex: 10,
        }}
      >
        {images.map((img, i) => (
          <div
            key={`slide-${img.src}`}
            data-testid={`hero-slide-${i}`}
            className="absolute inset-0"
            style={{
              opacity: i === activeIdx ? 1 : 0,
              transition: 'opacity 900ms cubic-bezier(0.65,0.05,0.22,1)',
              zIndex: i === activeIdx ? 2 : 1,
            }}
          >
            <img
              src={img.src}
              srcSet={`${img.src400} 400w, ${img.src800} 800w, ${img.src} 2000w`}
              sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px"
              alt={img.alt}
              draggable={false}
              loading={i === centerIdx ? 'eager' : 'lazy'}
              decoding="async"
              className="w-full h-full object-cover select-none"
              style={{
                objectPosition: img.focal || 'center center',
                transform: i === activeIdx ? 'scale(1.04)' : 'scale(1.01)',
                transition: 'transform 6000ms ease-out',
              }}
            />
          </div>
        ))}
        {/* Readability gradient for the headline */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/78 via-black/8 to-black/48" />
      </div>

      {/* ============ CONTENT (headline anchored above both layers) ============ */}
      <div className="pointer-events-none absolute inset-0 z-40 flex items-end">
        <div className="w-full max-w-[1500px] mx-auto px-6 md:px-10 pb-16 md:pb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="pointer-events-auto max-w-[860px]">
            <div className="overflow-hidden">
              <SplitHeadline
                text={headline}
                active={copyIn}
                stagger={timing.letterStagger}
                className="font-display uppercase text-white leading-[0.95] tracking-[-0.01em] text-[40px] sm:text-[54px] md:text-[76px] lg:text-[88px] drop-shadow-[0_4px_28px_rgba(0,0,0,0.9)]"
              />
            </div>
          </div>
          <div
            className={`pointer-events-auto flex items-center gap-3 transition-all duration-700 ${copyIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
            style={{ transitionDelay: copyIn ? `${(Array.isArray(headline) ? headline.join(' ').length : headline.length) * timing.letterStagger + 160}ms` : '0ms' }}
          >
            <a
              href={cta.href}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-1.5 bg-[color:var(--green)] text-black hover:bg-white font-display uppercase tracking-[0.10em] text-[13px] md:text-[14px] px-6 md:px-7 py-3 md:py-3.5 rounded-md shadow-[0_0_32px_-6px_rgba(25,223,2,0.9)] transition-colors"
            >
              {cta.label}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
            </a>
            <button
              type="button"
              onClick={onOpenVault}
              data-testid="hero-vault-btn"
              className="hidden md:inline-flex items-center gap-1.5 bg-white/[0.06] text-white hover:text-[color:var(--green)] font-display uppercase tracking-[0.10em] text-[12.5px] px-5 py-3 rounded-md border border-white/20 hover:border-[color:var(--green)]/60 transition-colors"
            >
              Our Story
            </button>
          </div>
        </div>
      </div>

      {/* ============ THUMBNAIL ROW ============ */}
      <div
        className={`pointer-events-auto absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-50 flex gap-2 md:gap-3 transition-all duration-700 ${thumbsIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        aria-label="Chapters"
      >
        {images.map((img, i) => {
          const isActive = i === activeIdx;
          return (
            <button
              key={`thumb-${img.src}`}
              type="button"
              data-testid={`hero-thumb-${i}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={isActive ? 'true' : undefined}
              className="relative overflow-hidden rounded-[3px] transition-all duration-300 hover:scale-[1.04]"
              style={{
                width: isActive ? 76 : 58,
                height: isActive ? 52 : 40,
                border: isActive ? '1.5px solid var(--green)' : '1px solid rgba(255,255,255,0.35)',
                boxShadow: isActive ? '0 0 22px -2px rgba(25,223,2,0.55)' : '0 6px 24px -8px rgba(0,0,0,0.7)',
              }}
            >
              <img
                src={img.src400}
                srcSet={`${img.src400} 400w, ${img.src800} 800w`}
                sizes="80px"
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                style={{ objectPosition: img.focal || 'center center', opacity: isActive ? 1 : 0.72 }}
              />
              {!isActive && <div className="absolute inset-0 bg-black/25" />}
            </button>
          );
        })}
      </div>

      {/* Component-scoped styles */}
      <style>{`
        .hero-line {
          display: block;
          margin-bottom: 0.14em;
        }
        .hero-line:last-child { margin-bottom: 0; }
        .hero-word {
          display: inline-block;
          white-space: nowrap;
        }
        .hero-letter {
          display: inline-block;
          transform-origin: 50% 100%;
          transition: transform 780ms cubic-bezier(0.22, 0.72, 0.14, 1), opacity 560ms ease;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="ionic-grid-hero"] * {
            transition: none !important;
            animation: none !important;
          }
        }
        @media (max-width: 640px) {
          .hero--gridphase [data-testid="hero-col-0"],
          .hero--gridphase [data-testid="hero-col-4"] { display: none; }
          .hero--gridphase [data-testid="hero-col-1"] { left: 0% !important; width: 33.333% !important; }
          .hero--gridphase [data-testid="hero-col-2"] { left: 33.333% !important; width: 33.334% !important; }
          .hero--gridphase [data-testid="hero-col-3"] { left: 66.666% !important; width: 33.334% !important; }
        }
        /* Mobile landscape: short viewport (typical phones rotated:
           390–932 wide × 350–500 tall). Shrink the hero headline so all
           three stat lines are visible without scrolling and the photo
           reads through. Does not affect desktop or mobile portrait. */
        @media (orientation: landscape) and (max-height: 500px) {
          [data-testid="hero-headline"] {
            font-size: 30px !important;
            line-height: 0.96 !important;
          }
        }
      `}</style>
    </section>
  );
};

export default IonicGridHero;
